export const RATE_LIMIT_CODE = 'RATE_LIMIT_EXCEEDED';

export class RateLimitError extends Error {
  constructor({
    message,
    retryAfterSeconds = null,
    blockedUntil = null,
    limit = null,
    windowSeconds = null,
    tier = null,
    code = RATE_LIMIT_CODE,
  } = {}) {
    super(
      message ||
        formatRateLimitMessage({
          retryAfterSeconds,
          limit,
          windowSeconds,
        })
    );
    this.name = 'RateLimitError';
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.blockedUntil = blockedUntil;
    this.limit = limit;
    this.windowSeconds = windowSeconds;
    this.tier = tier;
  }
}

/**
 * @param {number} windowSeconds
 * @returns {string}
 */
function formatWindowPhrase(windowSeconds) {
  if (!windowSeconds || windowSeconds === 60) {
    return 'minute';
  }
  if (windowSeconds > 60 && windowSeconds % 60 === 0) {
    const minutes = windowSeconds / 60;
    return minutes === 1 ? 'minute' : `${minutes} minutes`;
  }
  return `${windowSeconds} seconds`;
}

/**
 * @param {number} retryAfterSeconds
 * @returns {string}
 */
function formatRetryPhrase(retryAfterSeconds) {
  if (!retryAfterSeconds || retryAfterSeconds === 60) {
    return 'about 1 minute';
  }
  if (retryAfterSeconds > 60 && retryAfterSeconds % 60 === 0) {
    const minutes = retryAfterSeconds / 60;
    return minutes === 1 ? 'about 1 minute' : `about ${minutes} minutes`;
  }
  return `about ${retryAfterSeconds} seconds`;
}

/**
 * Static limit/window context without retry timing (use with live countdown UI).
 * @param {{ limit?: number | null, windowSeconds?: number | null }} params
 * @returns {string}
 */
export function formatRateLimitContext({
  limit = null,
  windowSeconds = 60,
} = {}) {
  const windowPhrase = formatWindowPhrase(windowSeconds);

  if (limit) {
    return `You've reached the limit of ${limit} requests per ${windowPhrase}.`;
  }

  return 'Too many requests. Please wait before trying again.';
}

/**
 * @param {{ retryAfterSeconds?: number | null, limit?: number | null, windowSeconds?: number | null }} params
 * @returns {string}
 */
export function formatRateLimitMessage({
  retryAfterSeconds = null,
  limit = null,
  windowSeconds = 60,
} = {}) {
  const retryPhrase = formatRetryPhrase(retryAfterSeconds);
  const context = formatRateLimitContext({ limit, windowSeconds });

  if (limit) {
    return `${context} Please wait ${retryPhrase} before trying again.`;
  }

  if (retryAfterSeconds && retryAfterSeconds > 0) {
    return `You're doing that a bit too quickly. Please wait ${retryPhrase} and try again.`;
  }

  return 'Too many requests. Please wait a minute and try again.';
}

/**
 * @param {string | Date | null | undefined} blockedUntil
 * @returns {Date | null}
 */
export function parseBlockedUntil(blockedUntil) {
  if (!blockedUntil) {
    return null;
  }
  if (blockedUntil instanceof Date) {
    return Number.isNaN(blockedUntil.getTime()) ? null : blockedUntil;
  }
  const parsed = new Date(blockedUntil);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * @param {{ blockedUntil?: string | Date | null, retryAfterSeconds?: number | null }} params
 * @returns {Date}
 */
export function resolveRetryExpiresAt({
  blockedUntil = null,
  retryAfterSeconds = null,
} = {}) {
  const fromIso = parseBlockedUntil(blockedUntil);
  if (fromIso) {
    return fromIso;
  }
  return computeRetryExpiresAt(retryAfterSeconds);
}

/**
 * @param {number} retryAfterSeconds
 * @returns {Date}
 */
export function computeRetryExpiresAt(retryAfterSeconds) {
  const seconds =
    Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? retryAfterSeconds
      : 60;
  return new Date(Date.now() + seconds * 1000);
}

/**
 * @param {Date} expiresAt
 * @returns {number}
 */
export function getSecondsUntilRetry(expiresAt) {
  if (!(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) {
    return 0;
  }
  return Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / 1000));
}

/**
 * @param {number} seconds
 * @returns {string}
 */
export function formatCountdown(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * @param {Date} expiresAt
 * @returns {string}
 */
export function formatRetryAtTime(expiresAt) {
  if (!(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) {
    return '';
  }
  return expiresAt.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * @param {Response} response
 * @returns {number | null}
 */
export function parseRetryAfterSeconds(response) {
  const header = response?.headers?.get?.('Retry-After');
  if (!header) {
    return null;
  }

  const seconds = Number.parseInt(header, 10);
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds;
  }

  const dateMs = Date.parse(header);
  if (Number.isFinite(dateMs)) {
    return Math.max(Math.ceil((dateMs - Date.now()) / 1000), 1);
  }

  return null;
}

/**
 * @param {Response} response
 * @returns {Promise<RateLimitError>}
 */
export async function readRateLimitError(response) {
  const retryAfterSeconds =
    parseRetryAfterSeconds(response) ?? null;
  let body = {};

  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : {};
  } catch {
    body = {};
  }

  const limit = Number.isFinite(body.limit) ? body.limit : null;
  const windowSeconds = Number.isFinite(body.windowSeconds)
    ? body.windowSeconds
    : 60;
  const resolvedRetryAfter =
    Number.isFinite(body.retryAfterSeconds) && body.retryAfterSeconds > 0
      ? body.retryAfterSeconds
      : retryAfterSeconds;
  const blockedUntil =
    typeof body.blockedUntil === 'string' && body.blockedUntil
      ? body.blockedUntil
      : null;

  return new RateLimitError({
    message:
      body.error ||
      formatRateLimitMessage({
        retryAfterSeconds: resolvedRetryAfter,
        limit,
        windowSeconds,
      }),
    retryAfterSeconds: resolvedRetryAfter,
    blockedUntil,
    limit,
    windowSeconds,
    tier: typeof body.tier === 'string' ? body.tier : null,
    code: body.code || RATE_LIMIT_CODE,
  });
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRateLimitError(error) {
  return error instanceof RateLimitError || error?.code === RATE_LIMIT_CODE;
}

/**
 * @param {{ retryAfterSeconds?: number | null, limit?: number | null, windowSeconds?: number | null }} params
 * @returns {string}
 */
export function buildRateLimitedPagePath({
  blockedUntil = null,
  retryAfterSeconds = 60,
  limit = null,
  windowSeconds = 60,
  tier = null,
} = {}) {
  const params = new URLSearchParams();
  if (blockedUntil) {
    params.set('blockedUntil', blockedUntil);
  } else if (retryAfterSeconds) {
    params.set('retryAfter', String(retryAfterSeconds));
  }
  if (limit) {
    params.set('limit', String(limit));
  }
  if (windowSeconds) {
    params.set('window', String(windowSeconds));
  }
  if (tier) {
    params.set('tier', tier);
  }
  return `/auth/rate-limited?${params.toString()}`;
}
